import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../utils/supabase';
import { Button, StatusBadge } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import { FormField, Input, Select } from '../common/Form';
import { TOURNAMENT_NAME_MAX_LENGTH } from '../../services/tournamentService';
import { getTournamentStatusLabel } from '../../utils/tournamentStatusDisplay';
import { getBracketInfo } from '../../utils/tournamentStandings';
import { getScoringRulesSummary, normalizeScoringConfig } from '../../utils/gameScoreRules';

const ROUND_LABELS = {
  group: 'Group stage',
  round_of_16: 'Round of 16',
  quarter_final: 'Quarter finals',
  semi_final: 'Semi finals',
  final: 'Final'
};

const ROUND_ORDER = ['group', 'round_of_16', 'quarter_final', 'semi_final', 'final'];

const BEST_OF_OPTIONS = [
  { value: '1', label: 'Best of 1' },
  { value: '3', label: 'Best of 3' },
  { value: '5', label: 'Best of 5' }
];

const BEST_LOSERS_RANK_OPTIONS = [
  { value: 'avg_points_per_match', label: 'Avg points per match' },
  { value: 'point_difference', label: 'Point difference' },
  { value: 'points_scored', label: 'Most points scored' },
  { value: 'wins', label: 'Most wins' },
  { value: 'points_against', label: 'Fewest points conceded' }
];

function SettingsSection({ title, description, children }) {
  return (
    <Card variant="default" size="small" className="!p-0 overflow-visible">
      <div className="border-b border-border-medium px-4 py-3 sm:px-5">
        <Heading level={3} className="mb-0 text-base sm:text-lg">
          {title}
        </Heading>
        {description ? (
          <Text className="mt-1 text-sm text-text-secondary">{description}</Text>
        ) : null}
      </div>
      <div className="space-y-4 px-4 py-4 sm:px-5">{children}</div>
    </Card>
  );
}

/**
 * Inline tournament settings editor (Tournament detail “Settings” tab).
 */
const EditTournamentSettingsPanel = ({ tournament, onSave }) => {
  const [name, setName] = useState('');
  const [gameTypeId, setGameTypeId] = useState('1');
  const [gameTypes, setGameTypes] = useState({ doubles: [], singles: [] });
  const [numberOfCourts, setNumberOfCourts] = useState('2');
  const [defaultBestOf, setDefaultBestOf] = useState('1');
  const [roundFormats, setRoundFormats] = useState({});
  const [groupCount, setGroupCount] = useState('2');
  const [pointsTarget, setPointsTarget] = useState('21');
  const [scoreValidation, setScoreValidation] = useState('standard');
  const [tournamentDate, setTournamentDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [progressionTopN, setProgressionTopN] = useState('2');
  const [progressionBestThird, setProgressionBestThird] = useState(false);
  const [bestLosersPrimary, setBestLosersPrimary] = useState('avg_points_per_match');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: gtError } = await supabase
          .from('game_types')
          .select('*')
          .in('id', [1, 2, 3, 4, 5, 6, 7]);
        if (gtError) throw gtError;
        if (cancelled) return;

        const rows = (data || []).map((gt) => {
          if (gt.id === 6) return { ...gt, name: 'Open Doubles' };
          if (gt.id === 7) return { ...gt, name: 'Open Singles' };
          return gt;
        });

        setGameTypes({
          doubles: rows
            .filter((gt) => gt.is_doubles !== false)
            .sort((a, b) => (a.id === 6 ? 1 : b.id === 6 ? -1 : a.id - b.id)),
          singles: rows.filter((gt) => gt.is_doubles === false).sort((a, b) => a.id - b.id)
        });
      } catch (err) {
        console.error('[EditTournamentSettingsPanel] game types:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!tournament) return;
    setName(tournament.name || '');
    setGameTypeId(String(tournament.game_type_id || tournament.game_type?.id || 1));
    setNumberOfCourts((tournament.number_of_courts || 2).toString());
    setDefaultBestOf((tournament.default_best_of || 1).toString());
    setGroupCount((tournament.group_count || 2).toString());
    setPointsTarget(String(tournament.points_target ?? 21));
    setScoreValidation(tournament.score_validation || 'standard');
    setTournamentDate(tournament.tournament_date || '');
    setStartTime(tournament.start_time ? String(tournament.start_time).slice(0, 5) : '');
    setEndTime(tournament.end_time ? String(tournament.end_time).slice(0, 5) : '');
    setLocation(tournament.location || '');
    setProgressionTopN(String(tournament.progression_top_n ?? 2));
    setProgressionBestThird(!!tournament.progression_best_third);
    setBestLosersPrimary(
      Array.isArray(tournament.best_losers_ranking_order)
        ? tournament.best_losers_ranking_order[0]
        : 'avg_points_per_match'
    );
    const defaultVal = tournament.default_best_of ?? 1;
    setRoundFormats(
      Object.keys(ROUND_LABELS).reduce(
        (acc, round) => ({
          ...acc,
          [round]: tournament.round_formats?.[round] ?? defaultVal
        }),
        {}
      )
    );
    setError(null);
  }, [tournament]);

  const entryCount = tournament?.tournament_entries?.length ?? 0;
  const groupCountNum = tournament?.tournament_groups?.length ?? 0;
  const canChangeGameType = tournament?.status === 'draft' && entryCount === 0;

  const bracketInfo = useMemo(() => {
    if (groupCountNum <= 0) return null;
    return getBracketInfo(groupCountNum, parseInt(progressionTopN, 10) || 2);
  }, [groupCountNum, progressionTopN]);

  if (!tournament) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Tournament name is required');
      return;
    }
    if (trimmedName.length > TOURNAMENT_NAME_MAX_LENGTH) {
      setError(`Tournament name must be ${TOURNAMENT_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    setSaving(true);
    setError(null);

    const bestLosersOrder = [
      bestLosersPrimary,
      'avg_points_per_match',
      'point_difference',
      'points_scored',
      'wins',
      'points_against',
      'random'
    ].filter((c, i, arr) => arr.indexOf(c) === i);

    try {
      await onSave({
        name: trimmedName,
        ...(canChangeGameType ? { game_type_id: parseInt(gameTypeId, 10) } : {}),
        number_of_courts: parseInt(numberOfCourts, 10),
        default_best_of: parseInt(defaultBestOf, 10),
        round_formats: roundFormats,
        group_count: parseInt(groupCount, 10),
        points_target: parseInt(pointsTarget, 10),
        score_validation: scoreValidation,
        tournament_date: tournamentDate || null,
        start_time: startTime || null,
        end_time: endTime || null,
        location: location.trim() || null,
        progression_top_n: parseInt(progressionTopN, 10),
        progression_best_third: progressionBestThird,
        best_losers_ranking_order: bestLosersOrder
      });
    } catch (err) {
      console.error('Error saving tournament:', err);
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const hasMatches = tournament.tournament_matches?.length > 0;
  const hasGroups = tournament.tournament_groups?.length > 0;
  const groupCountChanged = hasGroups && parseInt(groupCount, 10) !== (tournament.group_count || 0);

  const roundsToShow = ROUND_ORDER.filter((round) => {
    if (round === 'group') {
      return (tournament.group_count || 0) > 0 || hasGroups;
    }
    return (tournament.tournament_matches || []).some((m) => m.round === round);
  });

  const scoringSummary = getScoringRulesSummary(
    normalizeScoringConfig({ points_target: parseInt(pointsTarget, 10), score_validation: scoreValidation })
  );

  const gameTypeOptions = [
    ...(gameTypes.doubles || []).map((gt) => ({ value: String(gt.id), label: gt.name })),
    ...(gameTypes.singles || []).map((gt) => ({ value: String(gt.id), label: gt.name }))
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2 px-0.5">
        <Heading level={3} className="mb-0 text-base sm:text-lg">
          Tournament settings
        </Heading>
        <StatusBadge variant="default" size="small">
          {getTournamentStatusLabel(tournament.status)}
        </StatusBadge>
      </div>

      {error ? (
        <Card variant="danger" size="small">
          <Typography variant="body" className="text-sm">
            {error}
          </Typography>
        </Card>
      ) : null}

      {hasMatches ? (
        <Card variant="warning" size="small">
          <Typography variant="body" className="text-sm">
            Changing courts won&apos;t reassign existing matches — regenerate fixtures if needed.
          </Typography>
        </Card>
      ) : null}

      {groupCountChanged ? (
        <Card variant="danger" size="small">
          <Typography variant="body" className="text-sm">
            Changing group count clears assignments and match results for groups.
          </Typography>
        </Card>
      ) : null}

      <SettingsSection title="General" description="Name, event type, and courts at the venue.">
        <FormField label="Tournament name" required>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Spring Championship 2026"
            maxLength={TOURNAMENT_NAME_MAX_LENGTH}
            required
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField label="Date">
            <Input
              type="date"
              value={tournamentDate}
              onChange={(e) => setTournamentDate(e.target.value)}
            />
          </FormField>
          <FormField label="Start time">
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </FormField>
          <FormField label="End time">
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </FormField>
        </div>

        <FormField label="Location">
          <Input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Venue or court"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label="Tournament type"
            helperText={
              canChangeGameType
                ? undefined
                : entryCount > 0
                  ? 'Locked while players or teams are registered.'
                  : 'Locked after the tournament has started.'
            }
          >
            {canChangeGameType && gameTypeOptions.length > 0 ? (
              <Select
                value={gameTypeId}
                onChange={(e) => setGameTypeId(e.target.value)}
                options={gameTypeOptions}
                required
              />
            ) : (
              <Input type="text" value={tournament.game_type?.name || '—'} disabled readOnly />
            )}
          </FormField>

          <FormField label="Courts" required>
            <Input
              type="number"
              min="1"
              max="10"
              value={numberOfCourts}
              onChange={(e) => setNumberOfCourts(e.target.value)}
              required
            />
          </FormField>
        </div>
      </SettingsSection>

      <SettingsSection title="Match format" description="How many games each match is played to.">
        <FormField label="Default format" required>
          <Select
            value={defaultBestOf}
            onChange={(e) => {
              const v = e.target.value;
              setDefaultBestOf(v);
              setRoundFormats((prev) =>
                Object.keys(ROUND_LABELS).reduce(
                  (acc, round) => ({
                    ...acc,
                    [round]: prev[round] ?? parseInt(v, 10)
                  }),
                  {}
                )
              );
            }}
            options={BEST_OF_OPTIONS}
            required
          />
        </FormField>

        {roundsToShow.length > 0 && (
          <FormField label="Per round">
            <div className="space-y-2 rounded-xl bg-surface-subtle/70 p-3">
              {roundsToShow.map((round) => (
                <div key={round} className="flex min-h-[44px] items-center justify-between gap-3">
                  <Text className="min-w-0 flex-1 text-sm text-text-secondary">{ROUND_LABELS[round]}</Text>
                  <Select
                    value={String(roundFormats[round] ?? defaultBestOf)}
                    onChange={(e) =>
                      setRoundFormats((prev) => ({ ...prev, [round]: parseInt(e.target.value, 10) }))
                    }
                    options={BEST_OF_OPTIONS}
                    className="w-auto min-w-[8.5rem]"
                  />
                </div>
              ))}
            </div>
          </FormField>
        )}
      </SettingsSection>

      <SettingsSection title="Scoring" description="Rally points and deuce rules for every match.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Points to win">
            <Select
              value={pointsTarget}
              onChange={(e) => setPointsTarget(e.target.value)}
              options={[
                { value: '21', label: '21' },
                { value: '15', label: '15' }
              ]}
            />
          </FormField>
          <FormField label="Deuce">
            <Select
              value={scoreValidation}
              onChange={(e) => setScoreValidation(e.target.value)}
              options={[
                { value: 'standard', label: 'Standard' },
                { value: 'no_deuce', label: 'No deuce' }
              ]}
            />
          </FormField>
        </div>
        <Text className="text-sm text-text-muted">{scoringSummary}</Text>
      </SettingsSection>

      {hasGroups && (
        <SettingsSection title="Groups & knockout" description="How many groups run, and who advances.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Number of groups" required>
              <Input
                type="number"
                min="1"
                max="8"
                value={groupCount}
                onChange={(e) => setGroupCount(e.target.value)}
                required
              />
            </FormField>

            <FormField label="Qualifiers per group">
              <Select
                value={progressionTopN}
                onChange={(e) => setProgressionTopN(e.target.value)}
                options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: `Top ${n}` }))}
              />
            </FormField>
          </div>

          {bracketInfo && !bracketInfo.isPowerOf2 && bracketInfo.bestLosersNeeded > 0 && (
            <div className="space-y-3 rounded-xl bg-surface-subtle/70 p-4">
              <Text className="text-sm text-text-secondary">
                Need {bracketInfo.bestLosersNeeded} more qualifier
                {bracketInfo.bestLosersNeeded !== 1 ? 's' : ''} for a power-of-two bracket.
              </Text>
              <label className="flex min-h-[44px] cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={progressionBestThird}
                  onChange={(e) => setProgressionBestThird(e.target.checked)}
                  className="h-4 w-4 rounded border-border-medium text-primary focus:ring-primary"
                />
                <Text className="text-sm">Include best runners-up</Text>
              </label>
              {progressionBestThird && (
                <FormField label="Rank runners-up by">
                  <Select
                    value={bestLosersPrimary}
                    onChange={(e) => setBestLosersPrimary(e.target.value)}
                    options={BEST_LOSERS_RANK_OPTIONS}
                  />
                </FormField>
              )}
            </div>
          )}

          {bracketInfo?.isPowerOf2 && (
            <Text className="text-sm text-text-muted">
              {groupCountNum} groups · top {progressionTopN} · {bracketInfo.totalQualified} teams qualify
            </Text>
          )}
        </SettingsSection>
      )}

      <div className="flex justify-end">
        <Button type="submit" variant="primary" size="medium" disabled={saving || !name.trim()}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
};

export default EditTournamentSettingsPanel;
